import { OktaAuth } from '@okta/okta-auth-js';
import { ConfigService } from './config.service';
import { clientEnvVars } from './environment/clientEnvVars';

interface FetchOptions {
  skipAuth?: boolean;
  params?: Record<string, string>;
  headers?: HeadersInit;
  body?: unknown;
}

export class FetchService {
  private static instance: FetchService;
  private readonly baseURL: string;
  private oktaAuth: OktaAuth | null = null;

  private constructor() {
    this.baseURL = clientEnvVars().NORA_API_ROOT_URL;
  }

  public static getInstance(): FetchService {
    if (!FetchService.instance) {
      FetchService.instance = new FetchService();
    }
    return FetchService.instance;
  }

  public setOktaAuthService(oktaAuth: OktaAuth): void {
    this.oktaAuth = oktaAuth;
  }

  private async getAuthHeaders(skipAuth = false): Promise<HeadersInit> {
    const headers: HeadersInit = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (!skipAuth && this.oktaAuth) {
      const accessToken = this.oktaAuth.getAccessToken();
      if (!accessToken) {
        console.warn('Cannot refresh the session');
        await this.oktaAuth.signOut({
          postLogoutRedirectUri: ConfigService.getInstance().getLogoutUrl(),
        });
        throw new Error('Session not active');
      }
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
  }

  private createUrl(path: string, params?: Record<string, string>): string {
    const url = new URL(path, this.baseURL);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null) {
          url.searchParams.append(key, value);
        }
      });
    }
    return url.toString();
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return undefined as T;
  }

  public async get<T>(path: string, options: FetchOptions = {}): Promise<T> {
    const { skipAuth = false, params, headers: customHeaders = {} } = options;

    const authHeaders = await this.getAuthHeaders(skipAuth);
    const url = this.createUrl(path, params);

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...authHeaders,
        ...customHeaders,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return this.parseResponse<T>(response);
  }

  public async getBlob(
    path: string,
    options: FetchOptions = {},
  ): Promise<Blob> {
    const { skipAuth = false, params, headers: customHeaders = {} } = options;

    const authHeaders = await this.getAuthHeaders(skipAuth);
    const url = this.createUrl(path, params);

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...authHeaders,
        ...customHeaders,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  public async post<T>(path: string, options: FetchOptions = {}): Promise<T> {
    const {
      skipAuth = false,
      params,
      headers: customHeaders = {},
      body,
    } = options;

    const authHeaders = await this.getAuthHeaders(skipAuth);
    const url = this.createUrl(path, params);

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...authHeaders,
        ...customHeaders,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const message =
        errorBody?.message || response?.statusText || 'Something went wrong';
      throw new Error(message);
    }

    return this.parseResponse<T>(response);
  }

  public async delete<T>(path: string, options: FetchOptions = {}): Promise<T> {
    const { skipAuth = false, params, headers: customHeaders = {} } = options;

    const authHeaders = await this.getAuthHeaders(skipAuth);
    const url = this.createUrl(path, params);

    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        ...authHeaders,
        ...customHeaders,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return this.parseResponse<T>(response);
  }

  public async put<T>(path: string, options: FetchOptions = {}): Promise<T> {
    const {
      skipAuth = false,
      params,
      headers: customHeaders = {},
      body,
    } = options;

    const authHeaders = await this.getAuthHeaders(skipAuth);
    const url = this.createUrl(path, params);

    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        ...authHeaders,
        ...customHeaders,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return this.parseResponse<T>(response);
  }

  public async patch<T>(path: string, options: FetchOptions = {}): Promise<T> {
    const {
      skipAuth = false,
      params,
      headers: customHeaders = {},
      body,
    } = options;

    const authHeaders = await this.getAuthHeaders(skipAuth);
    const url = this.createUrl(path, params);

    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        ...authHeaders,
        ...customHeaders,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const message =
        errorBody?.message || response?.statusText || 'Something went wrong';
      throw new Error(message);
    }

    return this.parseResponse<T>(response);
  }
}
